import { useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import LoadingWheel from "../../shared/LoadingWheel";
import { TextInput } from "../../shared/Inputs/TextInput";
import {
  IShippingAddress,
  IShippingDimensions,
} from "./../../../models/Shipping";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { NumberInput } from "../../shared/Inputs/NumberInput";
import { useQueryClient } from "@tanstack/react-query";

interface EditAddressProps {
  dimensions: IShippingDimensions;
  partId: string;
  onEditSuccess: (address: IShippingDimensions) => void;
  categoryId?: string;
  cols?: string;
  maxColSpan?: string;
}

export function EditDimensions({
  dimensions,
  partId,
  onEditSuccess,
  categoryId,
  cols = "grid-cols-4",
  maxColSpan = "col-span-4",
}: EditAddressProps) {
  const { t } = useTranslation("stock");

  const queryClient = useQueryClient();
  
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Shipping"));
  const { register, getValues, handleSubmit } = useForm<IShippingDimensions>({
    defaultValues: dimensions,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const updatePartDimensions = (data: IShippingDimensions) => {
    setError(undefined);

    if (!data.height || data.height == 0) {
      setError(t("Please fill in Height"));
      return;
    }

    if (!data.length || data.length == 0) {
      setError(t("Please fill in Length"));
      return;
    }

    if (!data.width || data.width == 0) {
      setError(t("Please fill in Width"));
      return;
    }

    if (!data.weight || data.weight == 0) {
      setError(t("Please fill in Weight"));
      return;
    }

    setSubmitting(true);

    patchPart.mutate(
      {
        ...data,
        id: partId,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          onEditSuccess(data);
          queryClient.invalidateQueries(["parts", categoryId, "defaultcategory"]);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  };

  return submitting ? (
    <div>
      <LoadingWheel />
    </div>
  ) : (
    <form
      className={`grid ${cols} gap-2`}
      onSubmit={handleSubmit(updatePartDimensions)}
    >
      <NumberInput
        label={t("Width (cm)")}
        register={register}
        registerName="width"
      />
      <NumberInput
        label={t("Height (cm)")}
        register={register}
        registerName="height"
      />
      <NumberInput
        label={t("Length (cm)")}
        register={register}
        registerName="length"
      />
      <NumberInput
        label={t("Weight (kg)")}
        register={register}
        registerName="weight"
      />
      {error && (
        <div className={`${maxColSpan} bg-red-100 text-red-600 px-4 py-2 rounded`}>
          {error}
        </div>
      )}
      <ButtonInput
        label={t("Save Dimensions")}
        isSubmit={false}
        onClick={() => updatePartDimensions(getValues())}
        classes={"mt-4"}
      />
    </form>
  );
}
